import type { Turn } from '../../sim/direction';
import type { MowerSpec, MowerState } from '../../sim/mower';

/** What the mower does at a tile center. */
export type Action = 'straight' | Turn;
export const ACTIONS: readonly Action[] = ['straight', 'left', 'right', 'reverse'];

/**
 * A tile-granular abstraction of a mower's turn rule: a small finite "rule state" r in
 * [0, ruleStates) plus a transition table. Mirrors src/sim/turnRules.ts exactly at tile centers so
 * that plans made here execute identically in the sim (verified by tests).
 */
export interface MotionModel {
  readonly ruleStates: number;
  /** Rule state after taking `action` at a center and arriving at the next one; -1 if forbidden. */
  next(r: number, action: Action): number;
  /** Rule state of a live mower sitting at a tile center, between sim steps. */
  fromMower(m: Readonly<MowerState>): number;
}

const A = { straight: 0, left: 1, right: 2, reverse: 3 } as const;

function tableModel(
  ruleStates: number,
  fill: (r: number, a: Action) => number,
  fromMower: (m: Readonly<MowerState>) => number,
): MotionModel {
  const table = new Int16Array(ruleStates * 4);
  for (let r = 0; r < ruleStates; r++) {
    for (const a of ACTIONS) table[r * 4 + A[a]] = fill(r, a);
  }
  return {
    ruleStates,
    next: (r, a) => table[r * 4 + A[a]] as number,
    fromMower,
  };
}

export function motionModelFor(spec: MowerSpec): MotionModel {
  const rule = spec.turnRule;
  switch (rule.kind) {
    case 'free90':
      return tableModel(
        1,
        (_r, a) => (a === 'reverse' && !rule.canReverse ? -1 : 0),
        () => 0,
      );

    case 'freeSpin':
      return tableModel(
        1,
        () => 0,
        () => 0,
      );

    case 'free90Pullback': {
      // r = whole tiles since the last pull-back, capped at k (k = ready).
      const tpt = spec.ticksPerTile;
      const k = Math.ceil(rule.cooldownTicks / tpt);
      return tableModel(
        k + 1,
        (r, a) => {
          if (a === 'reverse') return r === k ? Math.min(1, k) : -1;
          return Math.min(r + 1, k);
        },
        // Between steps the sim's next decision happens after one more cooldown decrement.
        (m) => k - Math.min(k, Math.max(0, Math.ceil((m.pullbackCooldown - 1) / tpt))),
      );
    }

    case 'turnSpacing': {
      // r = last * (n + 1) + t, last ∈ {none, left, right}, t = tiles since that turn (capped).
      const n = rule.sameDirectionMinTiles;
      const encode = (last: number, t: number) => last * (n + 1) + Math.min(t, n);
      return tableModel(
        3 * (n + 1),
        (r, a) => {
          const last = Math.floor(r / (n + 1));
          const t = r % (n + 1);
          // A reversal (Reverse Gear) leaves the turn history alone, like driving straight.
          if (a === 'reverse') return rule.canReverse ? encode(last, t + 1) : -1;
          if (a === 'straight') return encode(last, t + 1);
          const turn = a === 'left' ? 1 : 2;
          if (turn === last && t < n) return -1;
          return encode(turn, 1);
        },
        (m) => encode(m.lastTurn === 'left' ? 1 : m.lastTurn === 'right' ? 2 : 0, m.tilesSinceTurn),
      );
    }
  }
}
